"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, MapPin, Target, Users, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Compass, Building2, MousePointerClick } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
const chartData = [{
  month: "Jan",
  PeterboroughQueries: 3200,
  HighIntentClicks: 1400
}, {
  month: "Feb",
  PeterboroughQueries: 3800,
  HighIntentClicks: 1650
}, {
  month: "Mar",
  PeterboroughQueries: 4500,
  HighIntentClicks: 2100
}, {
  month: "Apr",
  PeterboroughQueries: 5100,
  HighIntentClicks: 2600
}, {
  month: "May",
  PeterboroughQueries: 6200,
  HighIntentClicks: 3200
}, {
  month: "Jun",
  PeterboroughQueries: 7400,
  HighIntentClicks: 4100
}];
const chartConfig = {
  PeterboroughQueries: {
    label: "Total Regional Search Queries",
    color: "var(--primary)"
  },
  HighIntentClicks: {
    label: "High-Intent Commercial Clicks",
    color: "var(--accent)"
  }
} satisfies ChartConfig;
const marketMetrics = [{
  id: "1",
  stat: "68%",
  label: "Commercial Local Intent",
  description: "Of Peterborough search queries now include explicit geographic or proximity modifiers.",
  badgeText: "High Buying Intent"
}, {
  id: "2",
  stat: "3.4x",
  label: "Organic Opportunity",
  description: "Higher lead conversion rate for local businesses appearing in Peterborough's top 3 local map pack.",
  badgeText: "Map Pack Advantage"
}, {
  id: "3",
  stat: "+145%",
  label: "B2B Search Growth",
  description: "Increase in logistics, engineering, and professional services search demand across the Peterborough A1 corridor.",
  badgeText: "Expanding Market"
}, {
  id: "4",
  stat: "82%",
  label: "Competitor Content Gap",
  description: "Of local Peterborough competitors rely on generic national pages instead of dedicated regional intent hubs.",
  badgeText: "Unmet Demand"
}];
const marketInsights = [{
  id: "1",
  title: "Peterborough Local Map Pack & Geotargeting",
  subtitle: "Capturing High-Value Local Intent",
  highlightText: "Blush Pink Highlight: 44% of local clicks go directly to the top 3 map results.",
  description: "Peterborough buyers skip generic national directories in favor of verified local experts. I optimize your Google Business Profile and technical geo-signals so you dominate local map packs for Peterborough, Hampton, Paston, and surrounding Cambridgeshire zones.",
  category: "Map & Local SEO"
}, {
  id: "2",
  title: "Regional B2B Lead Generation Hubs",
  subtitle: "A1 Corridor Commercial Search",
  highlightText: "Blush Pink Highlight: B2B decision makers search with specific regional project scope.",
  description: "Businesses located in Peterborough's commercial hubs—like Orton Southgate, Cypress Point, and Gateway Park—require tailored B2B search funnels. I engineer dedicated regional landing pages that turn search traffic into inbound proposal requests.",
  category: "B2B Expansion"
}, {
  id: "3",
  title: "Intent-Driven Consumer Search Behavior",
  subtitle: "Mobile & Voice Search Optimisation",
  highlightText: "Blush Pink Highlight: 71% of mobile commercial queries result in direct calls or visits within 24 hours.",
  description: "Modern Peterborough shoppers demand instant clarity, quick load speeds, and precise service answers. I structure clear content hierarchy and schema markup to ensure your site answers immediate buyer questions directly in search engines.",
  category: "Search Experience"
}];
const searchBehaviorTraits = [{
  id: "1",
  trait: "Direct 'SEO Consultant Peterborough' & Regional Service Queries"
}, {
  id: "2",
  trait: "High Demand for Transparent Pricing & Direct Consultant Access"
}, {
  id: "3",
  trait: "Shift Towards AI Search & Google Generative Summaries"
}, {
  id: "4",
  trait: "Multi-touch B2B Buying Cycles across Cambridgeshire & East of England"
}];
export default function PeterboroughMarketAnalysis() {
  const prefersReducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("1");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="peterboroughmarketanalysis" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 border-accent/40 bg-secondary/50 text-foreground px-3 py-1 text-xs sm:text-sm font-medium">
            <MapPin className="size-3.5 text-primary mr-1.5" />
            Regional Search Intelligence
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-primary font-serif mb-4">
            Peterborough SEO &amp; Local Search Market Landscape
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I analyze local Peterborough search behavior to help regional businesses capture high-intent traffic, outperform national competitors, and turn search impressions into qualified commercial leads.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketMetrics.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border-border/80 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 bg-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="bg-secondary/70 text-foreground text-[13px] font-normal">
                      {item.badgeText}
                    </Badge>
                    <TrendingUp className="size-4 text-accent" />
                  </div>
                  <CardTitle className="text-3xl sm:text-4xl font-bold text-primary font-mono">
                    {item.stat}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <h2 className="text-base font-semibold text-foreground mb-1">
                    {item.label}
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-16">
          
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent bg-secondary/80 px-3 py-1 rounded-full border border-accent/20">
              <Compass className="size-3.5" />
              Peterborough Commercial Insights
            </div>

            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-primary font-serif leading-tight">
              Understanding How Peterborough Buyers Search Online
            </h2>

            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Peterborough is one of the UK’s fastest-growing cities, with rapid commercial development across logistics, technology, and regional services. When local buyers and business leaders search Google, they rarely rely on broad terms. Instead, they look for verified regional expertise.
            </p>

            <div className="p-4 sm:p-5 rounded-lg bg-secondary/40 border-l-4 border-accent text-foreground space-y-2">
              <span className="text-xs font-bold uppercase tracking-wide text-accent block">
                Local Search Trait Highlight
              </span>
              <p className="text-xs sm:text-sm font-medium leading-relaxed">
                <span className="bg-accent/20 px-1.5 py-0.5 rounded text-foreground font-semibold">
                  Blush Pink Highlight:
                </span>{" "}
                Peterborough customers convert <span className="font-semibold text-primary">2.4x faster</span> when landing on a targeted local page rather than a generic UK homepage.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider text-muted-foreground">
                Key Customer Search Patterns:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {searchBehaviorTraits.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-foreground">
                      {item.trait}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                <Link to="/ContactPage">
                  Book a Free Peterborough SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary">
                <Link to="/LocalSEOPage">
                  Explore Local SEO Services
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <Card className="border-border/80 shadow-md bg-card overflow-hidden">
              <CardHeader className="bg-muted/40 border-b border-border/60 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-md bg-primary/10 text-primary">
                      <BarChart3 className="size-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base sm:text-lg text-primary font-serif font-semibold">
                        Peterborough Local Search Intent Growth
                      </CardTitle>
                      <CardDescription className="text-xs text-muted-foreground">
                        Monthly query volume vs high-intent commercial clicks (Estimated)
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-accent/30 bg-accent/10 text-foreground text-xs">
                    Cambridgeshire Data
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <ChartContainer config={chartConfig} className="h-64 sm:h-72 w-full">
                  <AreaChart data={chartData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillQueries" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(220 80% 30%)" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="hsl(220 80% 30%)" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="fillClicks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(340 75% 65%)" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="hsl(340 75% 65%)" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                    <YAxis tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                    <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                    <Area type="monotone" dataKey="PeterboroughQueries" stroke="hsl(220 80% 30%)" strokeWidth={2} fillOpacity={1} fill="url(#fillQueries)" />
                    <Area type="monotone" dataKey="HighIntentClicks" stroke="hsl(340 75% 65%)" strokeWidth={2} fillOpacity={1} fill="url(#fillClicks)" />
                  </AreaChart>
                </ChartContainer>

                <div className="mt-4 pt-4 border-t border-border/60 flex flex-wrap items-center justify-between text-xs text-muted-foreground gap-2">
                  <div className="flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-primary inline-block" />
                    <span>Regional Queries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-accent inline-block" />
                    <span className="font-medium text-foreground">Commercial High-Intent Clicks</span>
                  </div>
                  <span className="text-[13px] italic">Verified Search Trends</span>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>

        <Separator className="my-12 sm:my-16" />

        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-semibold text-primary font-serif mb-2">
              Capitalising on Peterborough's Market Opportunities
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Working directly with me as your dedicated SEO consultant means applying tailored strategies designed specifically for Peterborough's competitive eco-system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {marketInsights.map((pillar, i) => <div key={pillar.id} data-index={i}>
                <Card className={`h-full border-border/80 shadow-sm transition-all duration-300 hover:shadow-md cursor-pointer ${activeTab === pillar.id ? "border-primary ring-1 ring-primary/20 bg-card" : "bg-card/70 hover:bg-card"}`} onClick={() => setActiveTab(pillar.id)}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-normal">
                        {pillar.category}
                      </Badge>
                      <Target className="size-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-primary font-serif">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-xs text-foreground/80 font-medium">
                      {pillar.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <div className="p-2.5 rounded bg-secondary/50 border-l-2 border-accent text-xs">
                      <span className="font-semibold text-foreground block mb-0.5">
                        Strategic Takeaway:
                      </span>
                      <span className="text-muted-foreground">
                        {pillar.highlightText}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        <div className="mt-16 sm:mt-20 rounded-2xl bg-gradient-to-r from-primary/10 via-background to-secondary/60 border border-primary/20 p-6 sm:p-10 lg:p-12 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-8 space-y-3">
              <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs">
                Direct Consultant Access
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-semibold text-primary font-serif">
                Ready to Dominate Peterborough Search Rankings?
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                You work directly with me—Yassine Essaadi—not a junior account manager or rotating agency team. I build bespoke, high-converting SEO strategies tailored specifically to your Peterborough business goals.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-card w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  View Verified Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}